import {
    compareScopeOrderTiebreak,
    ScopeMemberKind,
} from "./chartScopeOrdering";

// `sortOrder` is numeric(20,10). Placement runs entirely in scaled integers so
// a midpoint is exact: the column holds 20 significant digits and an IEEE
// double does not, and `DecimalColumn` reads the column back through
// `parseFloat`.
export const SORT_ORDER_SCALE = BigInt(10) ** BigInt(10);
const SORT_ORDER_FRACTION_DIGITS = 10;
// |sortOrder| < 1e10, i.e. the 10 integer digits numeric(20,10) allows.
const SORT_ORDER_SCALED_LIMIT = BigInt(10) ** BigInt(20);
// Consecutive integers past this would no longer fit the integer part.
const MAX_REBALANCED_SCOPE_SIZE = 10 ** 10;

export interface ScaledScopeMember {
    id: string;
    kind: ScopeMemberKind;
    name: string;
    sortOrder: bigint;
}

export type ScopePlacedRow = Omit<ScaledScopeMember, "sortOrder">;

export interface SortOrderAssignment {
    id: string;
    sortOrder: string;
}

export type ScopePlacement =
    | { status: "placed"; assignments: SortOrderAssignment[] }
    | { status: "rebalanced"; assignments: SortOrderAssignment[] }
    | { status: "unrepresentable" };

interface PlanScopePlacementParams {
    // Current scope members EXCLUDING every row being placed.
    siblings: ScaledScopeMember[];
    // Order within the block is significant.
    placed: ScopePlacedRow[];
    insertIndex: number;
}

export const parseScaledSortOrder = (value: string): bigint => {
    const trimmed = value.trim();
    const isNegative = trimmed.startsWith("-");
    const unsigned = isNegative ? trimmed.slice(1) : trimmed;
    const [integerText, fractionText = ""] = unsigned.split(".");
    const paddedFraction = fractionText
        .padEnd(SORT_ORDER_FRACTION_DIGITS, "0")
        .slice(0, SORT_ORDER_FRACTION_DIGITS);
    const scaled =
        BigInt(integerText === "" ? "0" : integerText) * SORT_ORDER_SCALE +
        BigInt(paddedFraction);
    return isNegative ? -scaled : scaled;
};

export const formatScaledSortOrder = (value: bigint): string => {
    const isNegative = value < BigInt(0);
    const magnitude = isNegative ? -value : value;
    const integerPart = magnitude / SORT_ORDER_SCALE;
    const fraction = (magnitude % SORT_ORDER_SCALE)
        .toString()
        .padStart(SORT_ORDER_FRACTION_DIGITS, "0")
        .replace(/0+$/, "");
    const sign = isNegative ? "-" : "";
    return fraction.length > 0
        ? `${sign}${integerPart}.${fraction}`
        : `${sign}${integerPart}`;
};

const compareScaledScopeOrder = (
    left: ScaledScopeMember,
    right: ScaledScopeMember,
): number => {
    if (left.sortOrder !== right.sortOrder) {
        return left.sortOrder < right.sortOrder ? -1 : 1;
    }
    return compareScopeOrderTiebreak(left, right);
};

// The order a scope's rows sit in before anything is placed. Exported so the
// caller can turn a neighbour id into an insert index against the same order
// the placement uses.
export const orderScaledScopeMembers = (
    members: ScaledScopeMember[],
): ScaledScopeMember[] => [...members].sort(compareScaledScopeOrder);

interface SpreadBlockParams {
    before: bigint | null;
    after: bigint | null;
    count: number;
}

const spreadBlock = ({ before, after, count }: SpreadBlockParams): bigint[] => {
    const indices = Array.from({ length: count }, (_, index) => BigInt(index));
    if (before !== null && after !== null) {
        const step = (after - before) / BigInt(count + 1);
        return indices.map((index) => before + step * (index + BigInt(1)));
    }
    if (before !== null) {
        return indices.map(
            (index) => before + (index + BigInt(1)) * SORT_ORDER_SCALE,
        );
    }
    if (after !== null) {
        return indices.map(
            (index) => after - (BigInt(count) - index) * SORT_ORDER_SCALE,
        );
    }
    return indices.map((index) => index * SORT_ORDER_SCALE);
};

// Distinct numeric(20,10) rows that collapse to one IEEE double would reorder
// by kind/name the moment the client reads them back.
const hasFloatCollision = (values: bigint[]): boolean => {
    const scaledByFloat = new Map<number, bigint>();
    for (const value of values) {
        const asFloat = Number.parseFloat(formatScaledSortOrder(value));
        const seen = scaledByFloat.get(asFloat);
        if (seen !== undefined && seen !== value) {
            return true;
        }
        scaledByFloat.set(asFloat, value);
    }
    return false;
};

interface NeedsRebalanceParams {
    siblingSortOrders: bigint[];
    blockSortOrders: bigint[];
}

const needsRebalance = ({
    siblingSortOrders,
    blockSortOrders,
}: NeedsRebalanceParams): boolean => {
    const siblingSet = new Set(siblingSortOrders);
    const blockSet = new Set<bigint>();
    for (const value of blockSortOrders) {
        if (
            value >= SORT_ORDER_SCALED_LIMIT ||
            value <= -SORT_ORDER_SCALED_LIMIT
        ) {
            return true;
        }
        if (siblingSet.has(value) || blockSet.has(value)) {
            return true;
        }
        blockSet.add(value);
    }
    return hasFloatCollision([...siblingSortOrders, ...blockSortOrders]);
};

const rebalance = (orderedIds: string[]): ScopePlacement => {
    if (orderedIds.length >= MAX_REBALANCED_SCOPE_SIZE) {
        return { status: "unrepresentable" };
    }
    return {
        status: "rebalanced",
        assignments: orderedIds.map((id, index) => ({
            id,
            sortOrder: String(index),
        })),
    };
};

// Assigns persisted `sortOrder` values to a block of rows landing at
// `insertIndex` of its destination scope. Only the block moves unless the gap
// cannot hold it, in which case the whole scope is respaced to 0 .. n-1 —
// deterministic for the same membership, and never a merged-tree restamp.
export const planScopePlacement = ({
    siblings,
    placed,
    insertIndex,
}: PlanScopePlacementParams): ScopePlacement => {
    if (placed.length === 0) {
        return { status: "placed", assignments: [] };
    }
    const ordered = orderScaledScopeMembers(siblings);
    const boundedIndex = Math.min(Math.max(insertIndex, 0), ordered.length);
    const before =
        boundedIndex > 0 ? ordered[boundedIndex - 1].sortOrder : null;
    const after =
        boundedIndex < ordered.length ? ordered[boundedIndex].sortOrder : null;
    const blockSortOrders = spreadBlock({
        before,
        after,
        count: placed.length,
    });
    const siblingSortOrders = ordered.map((member) => member.sortOrder);
    if (needsRebalance({ siblingSortOrders, blockSortOrders })) {
        return rebalance([
            ...ordered.slice(0, boundedIndex).map((member) => member.id),
            ...placed.map((row) => row.id),
            ...ordered.slice(boundedIndex).map((member) => member.id),
        ]);
    }
    return {
        status: "placed",
        assignments: placed.map((row, index) => ({
            id: row.id,
            sortOrder: formatScaledSortOrder(blockSortOrders[index]),
        })),
    };
};
